import type { Layout, Layouts } from 'react-grid-layout';

// Tool/Window types that can be added to dashboard
export type ToolType =
  | 'wiseguy-alerts'
  | 'squeezo'
  | 'steamroom-levels'
  | 'upgrades-downgrades'
  | 'congress-trades'
  | 'insider-trades'
  | 'market-movers'
  | 'td-sequential'
  | 'niner-breadth'
  | 'gex-profile'
  | 'gex-ray'
  | 'gex-heatmap'
  | 'nq-scalper'
  | 'zeroday-breadth'
  | 'cumulative-premium'
  | 'fj-economic-calendar'
  | 'fj-voice'
  | 'fj-tickstrike'
  | 'fj-headlines'
  | 'discord-composer'
  | 'sentiment-control'
  | 'wsj-feed';

// Individual tool window instance
export interface ToolWindow {
  id: string; // Unique ID for this instance (e.g., "wiseguy-alerts-1", "td-sequential-2")
  type: ToolType; // Type of tool
  ticker?: string; // Current ticker (if applicable)
  config?: Record<string, any>; // Tool-specific configuration
}

// Single dashboard configuration
export interface Dashboard {
  id: number;
  name: string;
  isDefault: boolean;
  layout: Layout[]; // Legacy single layout (kept for backward compat)
  layouts?: Layouts; // Per-breakpoint responsive layouts
  windows: ToolWindow[]; // List of tool windows on this dashboard
}

/** Soft cap on saved layouts (open + closed). Closing a tab does not free a slot. */
export const MAX_DASHBOARDS = 20;

// Complete dashboard state stored in backend
export interface DashboardState {
  dashboards: Dashboard[];
  currentDashboardId: number;
  /** Tab order. Omitted in v1 state — treated as “every dashboard is open”. */
  openDashboardIds?: number[];
  version?: number; // For future schema migrations
}

// Template from API
export interface DashboardTemplate {
  id: number;
  name: string;
  slug: string;
  description: string;
  layout: Layout[];
  windows: ToolWindow[];
  sort_order: number;
  created_by_username: string | null;
  created_at: string;
  updated_at: string;
}

// Context type
export interface DashboardContextType {
  // State
  dashboards: Dashboard[];
  openDashboards: Dashboard[];
  closedDashboards: Dashboard[];
  currentDashboard: Dashboard | null;
  currentDashboardId: number;
  isLoading: boolean;
  isSaving: boolean;
  templates: DashboardTemplate[];

  // Dashboard management
  switchDashboard: (id: number) => void;
  /** Put a saved layout back on the tab strip and focus it. */
  openDashboard: (id: number) => void;
  /** Remove from tabs only — the layout stays in the library. */
  closeDashboard: (id: number) => void;
  /** Returns the new dashboard id, or null if the cap is reached. */
  createDashboard: (name?: string) => number | null;
  renameDashboard: (id: number, name: string) => void;
  setDefaultDashboard: (id: number) => void;
  deleteDashboard: (id: number) => void;
  resetDashboard: (id: number) => void;
  loadTemplate: (templateSlug: string, dashboardId?: number) => void;

  // Window management
  addWindow: (toolType: ToolType, config?: Record<string, unknown>) => void;
  removeWindow: (windowId: string) => void;
  updateWindowConfig: (windowId: string, config: Record<string, unknown>) => void;
  syncTicker: (ticker: string, windowIds: string[]) => void;
  removeAllWindows: (dashboardId?: number) => void;

  // Layout management
  updateLayout: (layout: Layout[]) => void;
  updateLayouts: (layouts: Layouts) => void;

  // Manual save/load
  saveToBackend: () => Promise<void>;
  loadFromBackend: () => Promise<void>;
}
